export const site = {
  brand: 'Interactive Talker',
  domain: 'interactivetalker.com',
  siteUrl: import.meta.env.VITE_SITE_URL ?? 'https://interactivetalker.com',
  /** Ruta pública de política de privacidad (landing estática). */
  privacyPolicyPath: '/politica-privacidad.html',
} as const;

export function privacyPolicyUrl(baseUrl: string = site.siteUrl): string {
  const root = baseUrl.replace(/\/$/, '');
  return `${root}${site.privacyPolicyPath}`;
}

export const demosPagePath = '/prueba-agentes';

/** Solicitud de demo comercial: abre WhatsApp de ventas (wa.me). */
const DEFAULT_WHATSAPP_SALES_URL =
  'https://wa.me/59892636358?text=' + encodeURIComponent('Hola');

export const demoRequestHref =
  import.meta.env.VITE_WHATSAPP_SALES_URL?.trim() || DEFAULT_WHATSAPP_SALES_URL;

export const navLinks = [
  { href: '/#producto', label: 'Producto' },
  { href: '/#canales', label: 'Canales' },
  { href: '/#ia', label: 'IA' },
  { href: '/#precios', label: 'Precios' },
  { href: '/#operadores', label: 'Operadores' },
  { href: demoRequestHref, label: 'Contacto', external: true },
] as const;

export const hero = {
  label: 'Plataforma de comunicación con IA',
  title: 'Conversaciones con clientes, evolucionadas con IA',
  subtitle:
    'Unifica WhatsApp, redes, Telegram y tu sitio web en un solo cerebro. Automatiza lo repetitivo, reserva con calendario integrado y pasa a humano con contexto completo.',
  ctaPrimary: 'Solicitar demo',
  ctaSecondary: 'Ver cómo funciona',
} as const;

/** Contenido de la zona de entrada (header + hero + confianza). */
export const heroEntry = {
  announcement: {
    enabled: false,
    text: 'Nuevo: wizard para crear tu agente en minutos',
    href: '#inicio',
  },
  eyebrow: 'Agentes IA para atención y ventas',
  headline: 'Convierte más leads sin contratar más gente',
  titleAccent: 'Agentes IA que atienden 24/7',
  titleRest: '',
  title: 'Convierte más leads sin contratar más gente — Agentes IA que atienden 24/7',
  subtitle:
    'Automatiza el 80% de tus conversaciones en WhatsApp, Instagram y web. Tu equipo solo interviene cuando realmente importa.',
  ctaPrimary: 'Hablar por WhatsApp',
  ctaPrimaryHref: demoRequestHref,
  ctaSecondary: 'Prueba agentes IA',
  ctaSecondaryHref: demosPagePath,
  stats: [
    { value: '24/7', label: 'Cobertura' },
    { value: 'WhatsApp, Instagram, Web, etc.', href: '#canales' },
    { value: 'Escalado inteligente', label: 'Humano con contexto' },
  ] as const,
  trustTitle: 'Sectores que atienden por chat con Interactive Talker',
  trustLogos: [
    { name: 'Retail' },
    { name: 'Salud' },
    { name: 'Turismo' },
    { name: 'Educación' },
    { name: 'Servicios' },
  ],
} as const;

/** Video de fondo del hero (archivos en /public). */
export const heroBackgroundVideo = {
  enabled: true,
  mp4: '/hero.mp4',
  webm: '/hero.webm',
  poster: '/hero-poster.jpg',
} as const;

export type HeroChatMessage = {
  role: 'agent' | 'visitor';
  text: string;
  accent?: true;
  processing?: true;
  humanHandoff?: true;
  speakerName?: string;
  speakerAvatarUrl?: string;
  image?: string;
  imageAlt?: string;
};

export type HeroDemoCase = {
  id: string;
  agentName: string;
  description: string;
  visitorName: string;
  agentAvatarUrl: string;
  visitorAvatarUrl: string;
  script: HeroChatMessage[];
};

/** Casos de demo rotativos en el hero (mensajes + imágenes). */
export const heroDemoCases: HeroDemoCase[] = [
  {
    id: 'meraki',
    agentName: 'Agente Meraki',
    description: 'Agente de reserva de cabañas turísticas',
    visitorName: 'Benicio',
    agentAvatarUrl:
      'https://images.unsplash.com/photo-1587061949409-02df41d5e562?w=56&h=56&fit=crop&q=80',
    visitorAvatarUrl: 'https://i.pravatar.cc/56?img=33',
    script: [
      { role: 'agent', text: 'Hola Benicio, ¿en qué te puedo ayudar?' },
      {
        role: 'visitor',
        text: 'Hola, buscamos cabaña para 4 personas del 20 al 22 de junio.',
      },
      {
        role: 'agent',
        text: 'Perfecto. ¿Prefieres cerca del lago o más retirado del centro?',
      },
      { role: 'visitor', text: 'Cerca del lago, si se puede con parrillero.' },
      {
        role: 'agent',
        text: 'Esta es Cabaña Los Pinos — 4 personas, vista al lago y parrillero:',
        image:
          'https://images.unsplash.com/photo-1587061949409-02df41d5e562?w=480&q=80',
        imageAlt: 'Cabaña Los Pinos',
      },
      {
        role: 'visitor',
        text: 'Muy linda. ¿Esta otra que vi en Instagram está libre esas fechas?',
        image:
          'https://images.unsplash.com/photo-1771065999704-e133689927d0?w=480&q=80',
        imageAlt: 'Cabaña moderna con grandes ventanales',
      },
      {
        role: 'agent',
        text: 'Sí, es Cabaña del Bosque. Tiene 3 dormitorios y está a 200 m del lago. $4.200 la noche.',
      },
      { role: 'visitor', text: '¿Incluye ropa de cama y toallas?' },
      {
        role: 'agent',
        text: 'Sí, incluye ropa de cama, toallas y WiFi. La seña es el 30%.',
      },
      { role: 'visitor', text: 'Dale, reservala por favor. Mi mail es benicio@mail.com.' },
      {
        role: 'agent',
        text: 'Procesando... espere por favor.',
        processing: true,
      },
      {
        role: 'agent',
        text: '¡Listo! Reserva confirmada del 20 al 22. Te envío el detalle por WhatsApp.',
        accent: true,
      },
    ],
  },
  {
    id: 'handoff',
    agentName: 'Agente Soporte',
    description: 'Derivación a operador humano cuando el caso lo requiere',
    visitorName: 'Camila',
    agentAvatarUrl: '/logo.png',
    visitorAvatarUrl: 'https://i.pravatar.cc/56?img=45',
    script: [
      {
        role: 'visitor',
        text: 'Hice un pedido hace una semana y todavía no llegó. Quiero el reembolso.',
      },
      {
        role: 'agent',
        text: 'Lamento la demora, Camila. Reviso el estado del envío en el sistema.',
      },
      {
        role: 'agent',
        text: 'Procesando... espere por favor.',
        processing: true,
      },
      {
        role: 'agent',
        text: 'Hay un inconveniente con la paquetería. Te derivo con un operador para gestionar el reembolso.',
      },
      { role: 'visitor', text: 'Dale, gracias.' },
      {
        role: 'agent',
        speakerName: 'Salvador · Operador',
        speakerAvatarUrl: 'https://i.pravatar.cc/56?img=68',
        humanHandoff: true,
        text: 'Hola Camila, soy Salvador. Ya validé tu pedido #4821: iniciamos el reembolso hoy y lo ves en 48 h.',
        accent: true,
      },
      { role: 'visitor', text: 'Perfecto, gracias por tomarlo.' },
      {
        role: 'agent',
        speakerName: 'Salvador · Operador',
        speakerAvatarUrl: 'https://i.pravatar.cc/56?img=68',
        humanHandoff: true,
        text: 'De nada. Cualquier cosa escribime por acá: la conversación sigue en tu inbox.',
      },
    ],
  },
  {
    id: 'circula',
    agentName: 'Agente Circula',
    description: 'Búsqueda de ropa de segunda mano en catálogo',
    visitorName: 'Valentina',
    agentAvatarUrl:
      'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=56&h=56&fit=crop&q=80',
    visitorAvatarUrl: 'https://i.pravatar.cc/56?img=47',
    script: [
      { role: 'visitor', text: 'Hola, estoy buscando pantalones de hombre.' },
      {
        role: 'agent',
        text: '¡Hola Valentina! ¿Buscas algo en particular: jean, gabardina, deportivo?',
      },
      { role: 'visitor', text: 'Jean o gabardina, talle 32.' },
      {
        role: 'agent',
        text: 'Pantalon LEVIS — $1450 · Talle 32 · Jean · Muy buen estado',
        image:
          'https://images.unsplash.com/photo-1542272604-787c3835535d?w=480&q=80',
        imageAlt: 'Pantalon LEVIS',
      },
      { role: 'visitor', text: '¿Tienes algo en negro, mismo talle?' },
      {
        role: 'agent',
        text: 'Pantalon JACK & JONES — $550 · Talle 32 · Negro · Como nuevo',
        image:
          'https://images.unsplash.com/photo-1624378439575-d8705ad7ae80?w=480&q=80',
        imageAlt: 'Pantalon JACK & JONES negro',
      },
      { role: 'visitor', text: 'Me gusta el JACK & JONES. ¿Hacen envíos?' },
      {
        role: 'agent',
        text: 'Sí, envío a todo el país. El retiro en persona también está disponible en Circula.',
      },
      { role: 'visitor', text: 'Perfecto, quiero ese. ¿Cómo sigo?' },
      {
        role: 'agent',
        text: 'Procesando... espere por favor.',
        processing: true,
      },
      {
        role: 'agent',
        text: 'Te paso el link para ver fotos y completar la compra en Circula.',
        accent: true,
      },
    ],
  },
  {
    id: 'movil',
    agentName: 'Agente Móvil',
    description: 'Consultas y cambio de plan celular',
    visitorName: 'Lorenzo',
    agentAvatarUrl: '/logo.png',
    visitorAvatarUrl: 'https://i.pravatar.cc/56?img=12',
    script: [
      { role: 'agent', text: 'Hola Lorenzo, ¿cómo te puedo ayudar hoy?' },
      { role: 'visitor', text: 'Quería consultar mi plan. ¿Cuántos minutos me quedan para llamar?' },
      {
        role: 'agent',
        text: 'Tu plan actual es Básico 500. Te quedan 45 minutos hasta el 1 de junio.',
      },
      { role: 'visitor', text: 'Uf, me quedan pocos. ¿Qué opciones hay con más minutos?' },
      {
        role: 'agent',
        text: 'Tienes Básico 1000 por $5 más al mes, o ilimitado por $8 más. ¿Cuál te interesa?',
      },
      { role: 'visitor', text: 'El ilimitado suena bien. ¿Incluye llamadas a fijos también?' },
      {
        role: 'agent',
        text: 'Sí, llamadas ilimitadas a móviles y fijos del país. Se activa en el próximo ciclo o ya.',
      },
      { role: 'visitor', text: 'Actívamelo ya, por favor.' },
      {
        role: 'agent',
        text: 'Procesando... espere por favor.',
        processing: true,
      },
      {
        role: 'agent',
        text: 'Listo, ya tienes minutos ilimitados para llamar. Te llega confirmación por SMS.',
        accent: true,
      },
    ],
  },
  {
    id: 'turnos',
    agentName: 'Agente Salud',
    description: 'Agenda de turnos con calendario integrado',
    visitorName: 'María',
    agentAvatarUrl:
      'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=56&h=56&fit=crop&q=80',
    visitorAvatarUrl: 'https://i.pravatar.cc/56?img=32',
    script: [
      { role: 'agent', text: 'Hola María, ¿quieres agendar un turno?' },
      { role: 'visitor', text: 'Sí, necesito pediatría para mi hija de 3 años.' },
      {
        role: 'agent',
        text: '¿Es primera consulta o control de rutina?',
      },
      { role: 'visitor', text: 'Control de rutina, la vimos el mes pasado.' },
      {
        role: 'agent',
        text: 'Hay lugar mañana a las 10:30 o el jueves a las 16:00 con la Dra. Fernández.',
      },
      { role: 'visitor', text: 'Mañana 10:30 me viene bien. ¿Necesito llevar algo?' },
      {
        role: 'agent',
        text: 'Traé la libreta de vacunas y el carné de salud. La consulta dura unos 20 minutos.',
      },
      { role: 'visitor', text: 'Perfecto, confirmame ese horario.' },
      {
        role: 'agent',
        text: 'Procesando... espere por favor.',
        processing: true,
      },
      {
        role: 'agent',
        text: 'Turno confirmado para mañana 10:30. Te mando recordatorio por WhatsApp.',
        accent: true,
      },
    ],
  },
];

export const demoUseCasesSection = {
  id: 'inicio',
  label: 'Demos interactivas',
  title: 'Prueba agentes IA en acción',
  lead: 'Nueve casos de uso reales con demo web en vivo. Empieza por Agenda de turnos: responde en texto y también genera audio para escuchar.',
} as const;

export type DemoUseCaseHighlight = 'audio' | 'photos' | 'images' | 'processAudio';

export type DemoUseCase = {
  id: string;
  category: string;
  title: string;
  description: string;
  demoToken: string | null;
  icon: string;
  /** Badge + resalte visual de la card. */
  highlight?: DemoUseCaseHighlight;
};

const DEMO_HIGHLIGHT_META: Record<
  DemoUseCaseHighlight,
  { badge: string; cta: string; aria: string }
> = {
  audio: {
    badge: 'Genera audio',
    cta: 'Probar demo con audio',
    aria: 'Esta demo genera audio',
  },
  photos: {
    badge: 'Muestra fotos',
    cta: 'Probar demo con fotos',
    aria: 'Esta demo muestra fotos',
  },
  images: {
    badge: 'Procesa imágenes',
    cta: 'Probar demo con imágenes',
    aria: 'Esta demo procesa imágenes',
  },
  processAudio: {
    badge: 'Procesa audios',
    cta: 'Probar demo con audio',
    aria: 'Esta demo procesa notas de voz',
  },
};

export function demoHighlightMeta(highlight: DemoUseCaseHighlight | undefined) {
  return highlight ? DEMO_HIGHLIGHT_META[highlight] : null;
}

/** Casos de uso con enlace a demo web pública (token configurable vía VITE_DEMO_TOKEN_*). */
export const demoUseCases: DemoUseCase[] = [
  {
    id: 'salud',
    category: 'Salud',
    title: 'Agenda de turnos',
    description:
      'Reserva de consultas con calendario. Responde en texto y genera audio: escucha al agente hablar.',
    demoToken: '4e41e2e5-401a-4ead-a7ef-63b007b1a527',
    icon: 'calendar',
    highlight: 'audio',
  },
  {
    id: 'turismo',
    category: 'Turismo',
    title: 'Reserva de cabañas',
    description:
      'Disponibilidad, fotos de alojamientos y confirmación de reserva sin intervención humana.',
    demoToken: '4650788f-5774-418e-8d62-011fc1a91957',
    icon: 'calendar',
    highlight: 'photos',
  },
  {
    id: 'circula',
    category: 'Retail',
    title: 'Catálogo de ropa circular',
    description:
      'Búsqueda de prendas por talle, color y precio con imágenes y link de compra.',
    demoToken: '101aa719-4f15-4113-8736-909748807048',
    icon: 'plugin',
    highlight: 'images',
  },
  {
    id: 'telco',
    category: 'Telco',
    title: 'Consultas de plan móvil',
    description:
      'Saldo de minutos, cambio de plan y upgrades. Envía una nota de voz: el agente procesa el audio.',
    demoToken: 'c2725951-51fe-4991-8bf8-a6a26a02b13e',
    icon: 'mobile',
    highlight: 'processAudio',
  },
  {
    id: 'educacion',
    category: 'Educación',
    title: 'Atención al estudiante',
    description:
      'Consultas de admisión, aranceles y estado académico con respuestas inmediatas.',
    demoToken: '38892608-06a9-42d9-ab47-f7a320f83d4b',
    icon: 'learn',
  },
  {
    id: 'ventas',
    category: 'Ventas',
    title: 'Calificación inbound',
    description:
      'Captura de leads por WhatsApp o web y derivación al vendedor con contexto completo.',
    demoToken: '5628bde9-3d02-4ef3-ae2e-1b3cb4af29da',
    icon: 'flow',
  },
  {
    id: 'cobranza',
    category: 'Cobranza',
    title: 'Recordatorio de pagos',
    description:
      'Seguimiento de deuda, promesas de pago y links de checkout en la conversación.',
    demoToken: 'efff2bf6-b6c7-41e9-822d-205b7215c414',
    icon: 'bell',
  },
  {
    id: 'logistica',
    category: 'Logística',
    title: 'Estado de entregas',
    description:
      'Tracking de pedidos, ventanas de entrega y escalado cuando hay incidencias.',
    demoToken: '900fac69-3998-4a3f-8859-fc3a7814eed1',
    icon: 'inbox',
  },
  {
    id: 'atencion',
    category: 'Atención',
    title: 'Soporte y FAQ 24/7',
    description:
      'Respuestas desde tu base de conocimiento y handoff a operador con historial.',
    demoToken: '1df06e80-6abb-4d85-aede-b0a58ee822cd',
    icon: 'agent',
  },
];

export const painPoints = {
  title: '¿Te suena familiar?',
  lead: 'No es culpa de tu equipo: es imposible estar 24/7 en todos los canales sin un proceso conversacional optimizado.',
  items: [
    {
      icon: '😤',
      title: '"Siempre respondo lo mismo"',
      body: 'Tu equipo copia y pega las mismas respuestas todo el día. Horarios, precios, disponibilidad… puro tiempo perdido.',
      costLabel: 'Costo: horas semanales en tareas repetitivas',
    },
    {
      icon: '😱',
      title: '"Se nos escapan clientes"',
      body: 'Responder en minutos vs. horas es la diferencia entre cerrar o perder la venta. Fuera de horario, peor aún.',
      costLabel: 'Costo: leads sin respuesta a tiempo',
    },
    {
      icon: '🤯',
      title: '"No sabemos qué pasa"',
      body: 'Mensajes en varios canales y números, sin métricas unificadas. Imposible escalar sin datos.',
      costLabel: 'Costo: decisiones a ciegas',
    },
  ],
  cta: {
    text: 'No es tu culpa. Es imposible estar 24/7 en todos los canales. Por eso creamos Interactive Talker.',
    button: 'Ver cómo funciona',
    href: '#ia',
  },
} as const;

export const productShowcase = {
  title: 'Mira Interactive Talker en acción',
  lead: 'Una vista de la consola: inbox unificado, agentes IA y escalado a tu equipo con contexto completo.',
  ctaPrimary: 'Hablar por WhatsApp',
  ctaPrimaryHref: demoRequestHref,
  ctaSecondary: 'Probar ahora gratis',
  ctaSecondaryHref: demosPagePath,
} as const;

export const testimonials = {
  title: 'Resultados reales de equipos como el tuyo',
  lead: 'Casos representativos por industria. Los nombres se omiten hasta contar con autorización explícita.',
  items: [
    {
      industry: 'Clínica de salud',
      role: 'Operaciones',
      quote:
        'Pasamos de perder consultas fuera de horario a tener cobertura continua. El agente agenda turnos y el equipo solo confirma.',
      metrics: ['Cobertura fuera de horario', 'Menos consultas repetitivas'],
    },
    {
      industry: 'Retail online',
      role: 'Ventas',
      quote:
        'Las preguntas de stock y envíos ya no saturan al equipo. La IA responde al instante y deriva cuando hay intención de compra.',
      metrics: ['Respuestas instantáneas', 'Más tiempo para cerrar ventas'],
    },
    {
      industry: 'Turismo y hospedaje',
      role: 'Reservas',
      quote:
        'Consultas de disponibilidad y precios a cualquier hora. El calendario integrado reduce el ida y vuelta con huéspedes.',
      metrics: ['Reservas con menos fricción', 'Inbox unificado'],
    },
  ],
} as const;

export const ctaFinal = {
  title: 'Empieza a automatizar hoy',
  lead: 'Demo de 30 minutos sin compromiso. Te mostramos la plataforma funcionando con un agente configurado para tu negocio.',
  options: [
    {
      icon: '🚀',
      title: 'Demo personalizada',
      description: 'Escríbenos por WhatsApp y coordinamos',
      cta: 'Hablar por WhatsApp',
      href: demoRequestHref,
    },
    {
      icon: '⚡',
      title: 'Prueba inmediata',
      description: 'Prueba agentes IA en 2 minutos',
      cta: 'Empezar gratis',
      href: demosPagePath,
    },
  ],
  guarantees: ['Sin tarjeta de crédito', 'Onboarding incluido', 'Soporte en español'],
} as const;

export const comparison = {
  title: '¿Por qué Interactive Talker?',
  lead: 'Compara capacidades clave frente a otras plataformas de chat y automatización.',
  columns: ['Característica', 'Otras plataformas', 'Interactive Talker'],
  rows: [
    { feature: 'Entrenamiento desde el chat', others: 'No habitual', ours: 'Sí, con 👍👎' },
    { feature: 'App móvil para operadores', others: 'Suele ser extra', ours: 'Desde plan Pro' },
    { feature: 'Inbox multicanal unificado', others: 'Parcial', ours: 'Sí, todos los canales' },
    { feature: 'Flujos visuales + IA', others: 'Limitado', ours: 'Editor visual incluido' },
    { feature: 'Escalado a humano con contexto', others: 'Variable', ours: 'Historial completo' },
    { feature: 'Onboarding gratuito', others: 'A menudo de pago', ours: 'En todos los planes' },
  ],
} as const;

export const trustBadges = [
  { src: '/badges/ssl-secure.svg', alt: 'Conexión segura SSL' },
  { src: '/badges/uptime.svg', alt: 'Alta disponibilidad' },
  { src: '/badges/encrypted.svg', alt: 'Datos cifrados en tránsito' },
] as const;

export const pillars = [
  {
    id: 'equipo',
    tag: 'Equipo al mando',
    title: 'Tu operación, un solo panel',
    body: 'Consola web con inbox unificado, modo automático u humano y métricas de actividad. Tu equipo decide cuándo la IA responde y cuándo tomar el control.',
    highlights: ['Inbox multicanal', 'Modo humano con contexto', 'Dashboard de actividad'],
  },
  {
    id: 'asiste',
    tag: 'IA que asiste',
    title: 'Agentes, flujos y aprendizaje continuo',
    body: 'Instrucciones por agente, respuestas desde tus documentos (PDF, FAQs), atajos semánticos y editor visual de flujos. Menos costo por respuesta y más consistencia de marca.',
    highlights: ['Múltiples modelos de IA', 'Flujos visuales', 'Atajos semánticos'],
  },
  {
    id: 'resuelve',
    tag: 'IA que resuelve',
    title: 'Cobertura 24/7 con escalado inteligente',
    body: 'Widget en tu web, respuestas instantáneas y derivación al operador con historial, notificaciones y plugins (calendario, archivos).',
    highlights: ['Widget embebible', 'Plugins por agente', 'Escalado con historial'],
  },
] as const;

export const features = [
  {
    title: 'Agentes virtuales',
    description: 'Prompts, modelos OpenAI, Anthropic o DeepSeek. Activa o pausa agentes por campaña.',
    icon: 'agent',
  },
  {
    title: 'Multicanal',
    description:
      'WhatsApp Cloud, Messenger, Instagram, Telegram, chat web, email y más conexiones desde la consola.',
    icon: 'channels',
  },
  {
    title: 'Conversaciones',
    description: 'Historial completo, filtros y transición fluida entre modo automático y humano.',
    icon: 'inbox',
  },
  {
    title: 'Flujos visuales',
    description: 'Editor de workflows con publicación controlada para orquestar la experiencia.',
    icon: 'flow',
  },
  {
    title: 'Aprendizajes',
    description:
      'Entrena desde el chat con 👍 y 👎: atajos semánticos, guías de respuesta y menos llamadas al modelo en consultas repetidas.',
    icon: 'learn',
  },
  {
    title: 'Calendario',
    description: 'Recursos, disponibilidad, reservas, señas y revisión de comprobantes desde la consola.',
    icon: 'calendar',
  },
  {
    title: 'Notificaciones',
    description: 'Campana para operadores: atención humana, señas de calendario y más tipos configurables.',
    icon: 'bell',
  },
  {
    title: 'App móvil',
    description: 'Aplicación para operadores: chats, calendario y notificaciones push en el bolsillo.',
    icon: 'mobile',
  },
  {
    title: 'Roles y permisos',
    description: 'RBAC por empresa para consola web y app móvil: quién ve, quién opera, quién configura.',
    icon: 'shield',
  },
  {
    title: 'Plugins',
    description: 'Calendario, almacenamiento S3 por agente y arquitectura extensible para más capacidades.',
    icon: 'plugin',
  },
  {
    title: 'Demo embebida',
    description: 'Prueba el agente en tu sitio antes de producción con embed y clave de sitio.',
    icon: 'demo',
  },
  {
    title: 'Analítica',
    description: 'Resumen de conversaciones, mensajes y uso de atajos en el panel de inicio.',
    icon: 'chart',
  },
] as const;

export type ChannelItem = {
  id:
    | 'whatsapp_meta'
    | 'messenger'
    | 'instagram'
    | 'telegram'
    | 'web_widget'
    | 'email'
    | 'sms_twilio'
    | 'slack'
    | 'teams';
  name: string;
  detail?: string;
};

export const channels = {
  title: 'Un solo cerebro, todos tus canales',
  lead: 'Conecta donde están tus clientes. La IA y tu equipo ven la misma conversación en un solo inbox.',
  /** Canales con automatización habitual en producción */
  primary: [
    { id: 'whatsapp_meta', name: 'WhatsApp Cloud', detail: 'API oficial Meta' },
    { id: 'messenger', name: 'Messenger', detail: 'Meta' },
    { id: 'instagram', name: 'Instagram', detail: 'Mensajes directos' },
    { id: 'telegram', name: 'Telegram', detail: 'Bot' },
    { id: 'web_widget', name: 'Chat web', detail: 'Widget en tu sitio' },
    { id: 'email', name: 'Email', detail: 'Entrada y salida' },
  ] satisfies ChannelItem[],
  /** Registro en consola; alcance según despliegue */
  alsoInConsole: [
    { id: 'sms_twilio', name: 'SMS', detail: 'Twilio' },
    { id: 'slack', name: 'Slack' },
    { id: 'teams', name: 'Microsoft Teams' },
  ] satisfies ChannelItem[],
} as const;

export const agentTraining = {
  label: 'Aprendizaje continuo',
  title: 'Entrena a tus agentes desde el chat',
  lead:
    'Tu equipo revisa conversaciones reales y refina respuestas con un gesto: sin reentrenar el modelo, el agente mejora en minutos.',
  steps: [
    {
      id: 'up',
      emoji: '👍',
      title: 'Respuesta útil',
      body: 'Guarda la pregunta del visitante y la respuesta del agente como atajo semántico. En chats similares responde al toque, sin volver a llamar al modelo.',
    },
    {
      id: 'down',
      emoji: '👎',
      title: 'Corregir y enseñar',
      body: 'Indica cómo debería haber respondido: el par visitante → respuesta ideal se suma a la guía del agente (few-shot en el prompt).',
    },
    {
      id: 'guide',
      emoji: '📋',
      title: 'Guía y detección automática',
      body: 'Edita pares manualmente en Aprendizajes o deja que la plataforma proponga atajos desde conversaciones frecuentes.',
    },
  ],
  chat: {
    windowTitle: 'Inbox · WhatsApp',
    status: 'Modo automático (IA)',
    messages: [
      { role: 'visitor' as const, text: '¿Cuánto cuesta el plan Starter y qué incluye?' },
      {
        role: 'agent' as const,
        text: 'Starter es para probar en tu web: 1 agente, widget y hasta 1.000 mensajes al mes. ¿Quieres que te contacte ventas?',
        highlight: 'feedback' as const,
      },
      { role: 'visitor' as const, text: 'Sí, mándame info por mail.' },
      {
        role: 'agent' as const,
        text: 'Perfecto. Déjame tu email y te enviamos el detalle en el día.',
      },
    ],
    calloutUp: '👍 Atajo semántico',
    calloutDown: '👎 A la guía del agente',
  },
} as const;

export const workflowCalendar = {
  title: 'Del mensaje a la reserva confirmada',
  lead: 'Flujos y calendario trabajan juntos: el agente cotiza, agenda y el operador valida cuando hace falta.',
  steps: [
    { label: 'Visitante escribe', detail: 'Por widget, WhatsApp o red social.' },
    { label: 'Agente + flujo', detail: 'IA responde, califica y dispara acciones.' },
    { label: 'Plugin calendario', detail: 'Disponibilidad, reserva y seña si aplica.' },
    { label: 'Operador', detail: 'Notificación, revisión y cierre con contexto.' },
  ],
} as const;

export const operators = {
  title: 'Operadores con superpoderes',
  lead: 'La app móvil y las notificaciones mantienen a tu equipo al día sin perder el hilo.',
  items: [
    {
      title: 'Handoff humano',
      body: 'Cuando el visitante pide persona o la IA detecta urgencia, tu equipo entra con todo el historial.',
    },
    {
      title: 'App móvil para tu equipo',
      body: 'Chats y calendario en el móvil, con permisos por rol y push para no perder leads.',
    },
    {
      title: 'Alertas configurables',
      body: 'Elige qué eventos generan notificación: humano pendiente, seña a revisar y más.',
    },
  ],
} as const;

/** Descuento al pagar el plan con facturación anual (sobre el precio mensual de referencia). */
export const PRICING_ANNUAL_DISCOUNT_PERCENT = 15;

export function formatPricingUsd(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function pricingAnnualMonthlyUsd(monthlyPriceUsd: number): number {
  return Math.round(monthlyPriceUsd * (1 - PRICING_ANNUAL_DISCOUNT_PERCENT / 100));
}

export function formatPricingMessages(count: number): string {
  return new Intl.NumberFormat('es-UY').format(count);
}

export type PricingPlanFeature = {
  title: string;
  detail?: string;
  items?: readonly string[];
};

export type PricingPlan = {
  id: string;
  name: string;
  tagline: string;
  monthlyPriceUsd: number;
  messagesPerMonth: number | null;
  messagesLabel?: string;
  features: readonly PricingPlanFeature[];
  cta: string;
  highlighted: boolean;
  badge?: string;
  bannerLabel?: string;
  whyIdeal?: readonly string[];
};

export const pricing = {
  label: 'Planes',
  title: 'Elige el plan que escala con tu operación',
  lead: 'Desde el primer agente hasta despliegues enterprise con soporte dedicado. Todos incluyen onboarding gratuito y actualizaciones de plataforma.',
  billingToggle: {
    monthly: 'Mensual',
    annual: 'Anual',
    annualBadge: `−${PRICING_ANNUAL_DISCOUNT_PERCENT}%`,
    annualHint: `Ahorra ${PRICING_ANNUAL_DISCOUNT_PERCENT}% pagando el año por adelantado`,
  },
  footnote:
    'Precios en USD por mes. Pago con tarjeta de crédito. Volúmenes altos y add-ons se cotizan en la demo.',
  plans: [
    {
      id: 'starter',
      name: 'Starter',
      tagline: 'Ideal para probar IA en tu web, sin canales sociales.',
      monthlyPriceUsd: 35,
      messagesPerMonth: 1_000,
      features: [
        { title: '1 agente virtual' },
        { title: 'Solo widget web' },
        { title: 'Respuestas desde FAQs y docs básicos' },
        { title: 'Onboarding self-serve' },
        { title: 'Soporte por email' },
      ],
      cta: 'Elegir Starter',
      highlighted: false,
    },
    {
      id: 'basic',
      name: 'Basic',
      tagline: 'Para atender un canal con volumen controlado e inbox humano.',
      monthlyPriceUsd: 59,
      messagesPerMonth: 3_000,
      features: [
        { title: '1 agente virtual' },
        { title: 'Widget web + 1 canal social' },
        { title: 'Inbox unificado y modo humano' },
        { title: 'Respuestas desde tus documentos', detail: 'PDF, FAQs y base de conocimiento' },
        { title: 'Onboarding gratuito' },
        { title: 'Soporte por email' },
      ],
      cta: 'Elegir Basic',
      highlighted: false,
    },
    {
      id: 'advanced',
      name: 'Advanced',
      tagline: 'Más canales, automatización y capacidad para equipos en crecimiento.',
      monthlyPriceUsd: 129,
      messagesPerMonth: 7_000,
      features: [
        { title: 'Hasta 3 agentes virtuales' },
        { title: 'WhatsApp, Telegram y redes Meta' },
        { title: 'Flujos visuales y atajos semánticos' },
        {
          title: '1 plugin personalizado',
          detail: 'Desarrollo o adaptación para tu negocio',
        },
        { title: 'Onboarding gratuito' },
      ],
      cta: 'Elegir Advanced',
      highlighted: false,
    },
    {
      id: 'pro',
      name: 'Pro',
      tagline: 'Operación completa con app móvil, roles y analítica avanzada.',
      monthlyPriceUsd: 249,
      messagesPerMonth: 18_000,
      badge: 'Recomendado',
      bannerLabel: 'Más elegido · Mejor relación precio-valor',
      whyIdeal: [
        'Atiendes alto volumen de chats al día',
        'Tienes equipo de ventas o soporte',
        'Necesitas app móvil para operadores',
      ],
      features: [
        { title: 'Hasta 10 agentes virtuales' },
        { title: 'Todos los canales + app móvil para operadores' },
        {
          title: 'Hasta 3 plugins con personalización',
          items: ['Integraciones a medida', 'Flujos y acciones custom'],
        },
        { title: 'Roles, permisos y analítica' },
        { title: 'Onboarding gratuito' },
        { title: 'Soporte prioritario' },
      ],
      cta: 'Elegir Pro',
      highlighted: true,
    },
    {
      id: 'vip',
      name: 'VIP',
      tagline: 'Enterprise con SLA, integraciones custom y volúmenes sin tope fijo.',
      monthlyPriceUsd: 499,
      messagesPerMonth: null,
      messagesLabel: 'Volumen de mensajes a medida',
      features: [
        { title: 'SLA garantizado y account manager' },
        { title: 'Soporte dedicado' },
        { title: 'Integraciones y plugins custom' },
        { title: 'Varias sucursales o marcas en un mismo acuerdo' },
        { title: 'Modelos y límites personalizados' },
        { title: 'Onboarding dedicado gratuito' },
      ],
      cta: 'Hablar con ventas',
      highlighted: false,
    },
  ] satisfies readonly PricingPlan[],
} as const;

export const socialProof = {
  title: 'Para equipos que venden y atienden por chat',
  lead:
    'Si tus clientes escriben para consultar, cotizar, reservar o hacer seguimiento, Interactive Talker centraliza esas conversaciones en un solo lugar.',
  industries: [
    'Retail y e-commerce',
    'Servicios profesionales',
    'Salud y clínicas',
    'Educación y capacitación',
    'Turismo y hospedaje',
    'Inmobiliaria y alquileres',
  ],
} as const;

export const faq = [
  {
    q: '¿Qué canales soportan?',
    a: 'En producción hoy: WhatsApp Cloud (API Meta), Facebook Messenger, Instagram Direct, Telegram, widget de chat para tu sitio y email. Desde la consola también puedes registrar SMS (Twilio), Slack y Microsoft Teams; confirmamos en la demo qué tan automatizado está cada uno en tu despliegue. Todas las integraciones se configuran con asistentes guiados paso a paso.',
  },
  {
    q: '¿Necesito desarrolladores para el widget?',
    a: 'No. Copias un script de embed y una clave de sitio desde la consola. Para dominios de producción vs prueba puedes configurar URLs según tu entorno.',
  },
  {
    q: '¿Cómo funciona la demo?',
    a: 'Escríbenos por WhatsApp desde cualquier botón “Hablar por WhatsApp”. Coordinamos una sesión sin compromiso y te mostramos la plataforma con un agente de ejemplo en tus canales o en un entorno de prueba.',
  },
  {
    q: '¿Puedo usar mi propio modelo de IA?',
    a: 'Sí. La plataforma soporta proveedores compatibles (OpenAI, Anthropic, DeepSeek) con configuración por organización y modelos por agente.',
  },
  {
    q: '¿Qué pasa cuando un cliente pide hablar con una persona?',
    a: 'Activas modo humano: la IA deja de responder, el operador recibe notificación y ve toda la conversación previa, incluyendo reservas o archivos compartidos.',
  },
  {
    q: '¿Incluye app para el equipo de ventas?',
    a: 'Sí. La aplicación móvil de administración permite operar chats y calendario desde el celular, con roles y notificaciones push.',
  },
  {
    q: '¿Qué incluye cada plan?',
    a: 'Todos los planes incluyen onboarding. Starter es para probar en el widget web (1 agente, hasta 1.000 mensajes). Basic suma un canal social, inbox humano y hasta 3.000 mensajes. Advanced agrega más canales, flujos, atajos semánticos y 1 plugin personalizado (hasta 7.000 mensajes). Pro desbloquea hasta 3 plugins, app móvil, roles y analítica (hasta 18.000 mensajes). VIP es enterprise con SLA, integraciones y volúmenes a medida. El precio publicado es mensual; con pago anual obtienes un 15% de descuento. El cobro es siempre por transacción con tarjeta de crédito.',
  },
  {
    q: '¿Es seguro para datos de clientes?',
    a: 'Arquitectura multi-tenant por empresa, permisos granulares y buenas prácticas de aislamiento. Los detalles de compliance se revisan en la demo según tu industria.',
  },
  {
    q: '¿Puedo integrar almacenamiento de archivos?',
    a: 'El plugin S3 conecta buckets por agente para que la IA consulte y gestione archivos en conversaciones, además de medios de calendario y adjuntos de chat.',
  },
] as const;

export const contact = {
  title: 'Solicita una demo gratuita',
  lead: 'Escríbenos por WhatsApp y coordinamos una demo sin compromiso.',
  privacyLabel: 'Acepto la política de privacidad y que Interactive Talker use mis datos para contactarme sobre el producto.',
  trustSignals: [
    'Tus datos están seguros',
    'Te respondemos en 2 horas',
    'Sin compromiso',
  ],
  whatsappSalesCta: 'Hablar por WhatsApp',
  whatsappSalesHint: 'Te respondemos por WhatsApp',
  whatsappSalesUrl: demoRequestHref,
} as const;

export const footer = {
  tagline: 'Comunicación con clientes, potenciada por IA.',
  legal: [
    { href: demoRequestHref, label: 'Contacto', external: true },
    { href: '/politica-privacidad.html', label: 'Política de privacidad' },
    { href: '#', label: 'Configurar cookies', action: 'cookie-settings' as const },
  ],
} as const;
